import { type MouseEvent, type ReactElement, type ReactNode, useState } from 'react';
import { ProjectIdentity } from '@/features/projects/components/ProjectIdentity';
import {
  buildProjectScopedPath,
  isProjectScopedProductPath,
  stripProjectRouteScope,
} from '@/features/projects/utils/projectScopedRoutes';
import { buildProjectSettingsHrefForProject } from '@/features/projects/utils/projectSettingsRoute';
import { getFilterEmptyLabel } from '@/shared/copy/filters';
import { getToastMessage } from '@/shared/copy/toasts';
import { Box, Text } from '@/ui';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import {
  AddIcon,
  BusinessIcon,
  Divider,
  ExpandIcon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  WorkOutline,
} from '@/ui/primitives';
import {
  buildTopBarContextButtonIconSlotSx,
  buildTopBarContextButtonSx,
  buildTopBarContextChevronSx,
  buildTopBarContextRootSx,
  topBarContextAddProjectActionSx,
  topBarContextButtonGap,
  topBarContextButtonLabelSx,
  topBarContextChevronSize,
  topBarContextDividerTextSx,
  topBarContextIconSx,
  topBarContextOrganisationMenuListSx,
  topBarContextOrganisationMenuTextSx,
  topBarContextPopoverPaperSx,
  topBarContextProjectButtonGap,
  topBarContextProjectChevronSize,
  topBarContextProjectIconSx,
  topBarContextProjectMenuListSx,
  topBarContextProjectPrimaryTextSx,
} from './TopBarContextControls.styles';
import type { NotificationContextValue } from '@/providers/notificationContext';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type ProjectOption = Pick<
  ProjectDto,
  'id' | 'name' | 'slug' | 'organisationId' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'
> & {
  locked: boolean;
  lockReason: string | null;
};

type TopBarContextControlsProps = {
  activePath: string;
  organisationLabel: string;
  selectedOrganisationId: string | null;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  projectLabel: string;
  selectedProjectId: string | null;
  projects: ProjectOption[];
  canCreateProject: boolean;
  lockProjectSelector: boolean;
  notify: NotificationContextValue['notify'];
  onNavigate: (path: string) => void;
  onAddProject: () => void;
  setSelectedOrganisationId: (value: string | null) => void;
  setSelectedProjectId: (value: string | null) => void;
};

type ActiveMenu = 'organisation' | 'project' | null;

const isProjectSettingsPath = (activePath: string): boolean =>
  activePath === '/project-settings' ||
  /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings$/.test(activePath);

const resolveProjectSwitchPath = ({
  activePath,
  organisations,
  project,
}: {
  activePath: string;
  organisations: TopBarContextControlsProps['organisations'];
  project: ProjectOption;
}): string => {
  const workspaceSlug =
    project.organisationId === null
      ? null
      : (organisations.find((organisation) => organisation.id === project.organisationId)?.slug ??
        null);

  if (isProjectSettingsPath(activePath)) {
    return buildProjectSettingsHrefForProject({
      projectId: project.id,
      projectSlug: project.slug,
      workspaceSlug,
    });
  }
  if (workspaceSlug !== null && isProjectScopedProductPath(activePath)) {
    return buildProjectScopedPath({
      workspaceSlug,
      projectSlug: project.slug,
      path: stripProjectRouteScope(activePath),
    });
  }
  return activePath;
};

const resolveAllProjectsSwitchPath = (activePath: string): string => {
  if (isProjectSettingsPath(activePath)) {
    return '/project-settings';
  }
  return '/execution-runs';
};

export const TopBarContextControls = ({
  activePath,
  organisationLabel,
  selectedOrganisationId,
  organisations,
  projectLabel,
  selectedProjectId,
  projects,
  canCreateProject,
  lockProjectSelector,
  notify,
  onNavigate,
  onAddProject,
  setSelectedOrganisationId,
  setSelectedProjectId,
}: TopBarContextControlsProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const useCompactContextControl = layoutMode !== layoutModeTokens.wide;
  const canOpenOrganisationMenu = organisations.length > 1;
  const canOpenProjectMenu = !lockProjectSelector;
  const projectItems = projects.length > 0 ? projects : [];
  const showAllProjectsOption = projectItems.length > 1;
  const selectedProject =
    selectedProjectId === null
      ? null
      : (projects.find((project) => project.id === selectedProjectId) ?? null);
  const showProjectMenuItems = activeMenu === 'project' && canOpenProjectMenu;

  const openMenu = (event: MouseEvent<HTMLElement>, menu: Exclude<ActiveMenu, null>): void => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menu);
  };

  const closeMenu = (): void => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const renderContextButton = ({
    label,
    labelContent = null,
    icon,
    disabled,
    expanded,
    controlsId,
    showChevron = true,
    chevronSize = topBarContextChevronSize,
    gap = topBarContextButtonGap,
    onClick,
  }: {
    label: ReactNode;
    labelContent?: ReactElement | null;
    icon: ReactElement | null;
    disabled: boolean;
    expanded: boolean;
    controlsId: string;
    showChevron?: boolean;
    chevronSize?: number;
    gap?: string;
    onClick: (event: MouseEvent<HTMLElement>) => void;
  }): ReactElement => (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      aria-controls={expanded ? controlsId : undefined}
      aria-expanded={expanded ? 'true' : undefined}
      aria-haspopup={disabled ? undefined : 'menu'}
      onClick={onClick}
      sx={buildTopBarContextButtonSx({ disabled, gap, useCompactContextControl })}
    >
      <Box component="span" sx={buildTopBarContextButtonIconSlotSx(icon !== null)}>
        {icon}
      </Box>
      {labelContent ?? (
        <Text role="bodyStrong" component="span" sx={topBarContextButtonLabelSx}>
          {label}
        </Text>
      )}
      {showChevron ? <ExpandIcon sx={buildTopBarContextChevronSx(chevronSize)} /> : null}
    </Box>
  );

  const renderOrganisationItems = (): ReactElement[] =>
    organisations.map((organisation) => (
      <MenuItem
        key={organisation.id}
        selected={organisation.id === selectedOrganisationId}
        onClick={() => {
          setSelectedOrganisationId(organisation.id);
          setSelectedProjectId(null);
          notify({
            message: getToastMessage('context.workspaceSwitched', {
              name: organisation.name,
            }),
            tone: 'blue',
          });
          closeMenu();
          onNavigate('/execution-runs');
        }}
      >
        <ListItemIcon>
          <BusinessIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={organisation.name} sx={topBarContextOrganisationMenuTextSx} />
      </MenuItem>
    ));

  const renderProjectItems = (): ReactElement[] =>
    projectItems.map((project) => (
      <MenuItem
        key={project.id}
        selected={project.id === selectedProjectId}
        onClick={() => {
          setSelectedProjectId(project.id);
          notify({
            message: project.locked
              ? getToastMessage('context.projectViewOnly', { name: project.name })
              : getToastMessage('context.projectSwitched', { name: project.name }),
            tone: project.locked ? 'orange' : 'blue',
          });
          closeMenu();
          onNavigate(resolveProjectSwitchPath({ activePath, organisations, project }));
        }}
      >
        <ProjectIdentity
          project={project}
          disabled={project.locked}
          secondaryText={project.lockReason}
        />
      </MenuItem>
    ));

  return (
    <>
      <Box sx={buildTopBarContextRootSx(useCompactContextControl)}>
        {renderContextButton({
          label: organisationLabel,
          icon: useCompactContextControl ? null : <BusinessIcon sx={topBarContextIconSx} />,
          disabled: !canOpenOrganisationMenu,
          expanded: activeMenu === 'organisation',
          controlsId: 'top-bar-organisation-menu',
          showChevron: canOpenOrganisationMenu,
          onClick: (event) => {
            openMenu(event, 'organisation');
          },
        })}
        <Text role="body" component="span" sx={topBarContextDividerTextSx}>
          /
        </Text>
        {renderContextButton({
          label: projectLabel,
          labelContent: (
            <ProjectIdentity
              project={selectedProject}
              fallbackIcon={<WorkOutline sx={topBarContextProjectIconSx} />}
              fallbackName={projectLabel}
            />
          ),
          icon: null,
          disabled: !canOpenProjectMenu,
          expanded: activeMenu === 'project',
          controlsId: 'top-bar-project-menu',
          chevronSize: topBarContextProjectChevronSize,
          gap: topBarContextProjectButtonGap,
          onClick: (event) => {
            openMenu(event, 'project');
          },
        })}
      </Box>
      <Popover
        open={Boolean(anchorEl && activeMenu)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        paperSx={topBarContextPopoverPaperSx}
      >
        {activeMenu === 'organisation' ? (
          <MenuList
            id="top-bar-organisation-menu"
            dense
            disablePadding
            sx={topBarContextOrganisationMenuListSx}
          >
            {renderOrganisationItems()}
          </MenuList>
        ) : null}
        {activeMenu === 'project' ? (
          <MenuList
            id="top-bar-project-menu"
            dense
            disablePadding
            sx={topBarContextProjectMenuListSx}
          >
            {showProjectMenuItems && showAllProjectsOption ? (
              <MenuItem
                onClick={() => {
                  setSelectedProjectId(null);
                  notify({ message: getToastMessage('context.viewingAllProjects'), tone: 'blue' });
                  closeMenu();
                  onNavigate(resolveAllProjectsSwitchPath(activePath));
                }}
              >
                <Text role="bodyStrong" component="span" sx={topBarContextProjectPrimaryTextSx}>
                  {getFilterEmptyLabel('allProjects')}
                </Text>
              </MenuItem>
            ) : null}
            {showProjectMenuItems ? renderProjectItems() : null}
            {showProjectMenuItems
              ? [
                  <Divider key="project-actions-divider" />,
                  <MenuItem
                    key="add-project"
                    disabled={!canCreateProject}
                    onClick={() => {
                      if (!canCreateProject) return;
                      closeMenu();
                      onAddProject();
                    }}
                  >
                    <Box sx={topBarContextAddProjectActionSx}>
                      <AddIcon fontSize="small" />
                      <Text
                        role="bodyStrong"
                        component="span"
                        sx={topBarContextProjectPrimaryTextSx}
                      >
                        Add New
                      </Text>
                    </Box>
                  </MenuItem>,
                ]
              : null}
          </MenuList>
        ) : null}
      </Popover>
    </>
  );
};
